import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Check, X } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import type { Plan, PlanFeature, ComparisonRow } from '@/_types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

// ---------------------------------------------------------------------------
// Plan Details schema
// ---------------------------------------------------------------------------

const planSchema = z.object({
  name: z.string().min(1, 'Name is required').max(128),
  slug: z.string().min(1, 'Slug is required').max(64).regex(/^[a-z0-9-]+$/, 'Lowercase, numbers and hyphens only'),
  tagline: z.string().min(1, 'Tagline is required').max(255),
  monthly_price: z.coerce.number().int().min(0),
  annual_price: z.coerce.number().int().min(0),
  credits_label: z.string().min(1, 'Credits label is required').max(128),
  lead_credits: z.coerce.number().int().min(0).nullable().optional(),
  email_credits: z.coerce.number().int().min(0).nullable().optional(),
  trial_days: z.coerce.number().int().min(0).max(365).default(14),
  ai_features: z.boolean().default(false),
  advanced_analytics: z.boolean().default(false),
  analytics_export: z.boolean().default(false),
  campaign_features: z.boolean().default(false),
  is_popular: z.boolean().default(false),
  is_active: z.boolean().default(true),
  sort_order: z.coerce.number().int().min(0).default(0),
  stripe_price_id_monthly: z.string().max(255).optional().or(z.literal('')),
  stripe_price_id_yearly: z.string().max(255).optional().or(z.literal('')),
  stripe_product_id: z.string().max(255).optional().or(z.literal('')),
  currency: z.string().max(10).default('USD'),
});

type PlanFormValues = z.infer<typeof planSchema>;

// ---------------------------------------------------------------------------
// Add Feature Dialog
// ---------------------------------------------------------------------------

function AddFeatureDialog({
  open,
  onClose,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (text: string, included: boolean) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [included, setIncluded] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    setText('');
    setIncluded(true);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) { toast.error('Feature text is required'); return; }
    setSaving(true);
    try {
      await onSave(text.trim(), included);
      handleClose();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Feature</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="feature-text">Feature Text</Label>
            <Input
              id="feature-text"
              placeholder="e.g. Unlimited email campaigns"
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>Included in Plan</Label>
            <Select
              value={included ? 'yes' : 'no'}
              onValueChange={(v) => setIncluded(v === 'yes')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes — included</SelectItem>
                <SelectItem value="no">No — not included</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Adding…' : 'Add Feature'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Add Comparison Row Dialog
// ---------------------------------------------------------------------------

function AddComparisonRowDialog({
  open,
  onClose,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (featureName: string, value: string) => Promise<void>;
}) {
  const [featureName, setFeatureName] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    setFeatureName('');
    setValue('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!featureName.trim()) { toast.error('Feature name is required'); return; }
    setSaving(true);
    try {
      await onSave(featureName.trim(), value.trim());
      handleClose();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Comparison Row</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="comp-feature">Feature Name</Label>
            <Input
              id="comp-feature"
              placeholder="e.g. Lead credits per month"
              value={featureName}
              onChange={(e) => setFeatureName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="comp-value">
              Value for this plan{' '}
              <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id="comp-value"
              placeholder="e.g. Unlimited / 500/mo / true / false"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Adding…' : 'Add Row'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Feature row component
// ---------------------------------------------------------------------------

function FeatureRow({
  feature,
  onUpdate,
  onDelete,
}: {
  feature: PlanFeature;
  onUpdate: (id: string, data: Partial<PlanFeature>) => void;
  onDelete: (id: string) => void;
}) {
  const [text, setText] = useState(feature.text);

  // Sync if prop changes (e.g. after refetch)
  useEffect(() => { setText(feature.text); }, [feature.text]);

  return (
    <div className="flex items-center gap-2 py-1.5">
      <button
        type="button"
        onClick={() => onUpdate(feature.id, { included: !feature.included })}
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
          feature.included
            ? 'bg-green-100 text-green-700 hover:bg-green-200'
            : 'bg-muted text-muted-foreground hover:bg-muted/80'
        }`}
        title={feature.included ? 'Mark as not included' : 'Mark as included'}
      >
        {feature.included ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      </button>
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => text !== feature.text && onUpdate(feature.id, { text })}
        className="flex-1 h-8 text-sm"
        placeholder="Feature text…"
      />
      <button
        type="button"
        onClick={() => onDelete(feature.id)}
        className="flex-shrink-0 p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main PlanFormPage
// ---------------------------------------------------------------------------

export default function PlanFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [addFeatureOpen, setAddFeatureOpen] = useState(false);
  const [addRowOpen, setAddRowOpen] = useState(false);

  // ── Plan details form ──────────────────────────────────────────────────────
  const { data: planData, isLoading: planLoading } = useQuery({
    queryKey: ['admin-plan', id],
    queryFn: async () => {
      const res = await apiClient.get(`/plans/${id}`);
      return res.data.data as Plan;
    },
    enabled: isEdit,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PlanFormValues>({ resolver: zodResolver(planSchema) });

  useEffect(() => {
    if (planData) {
      reset({
        name: planData.name,
        slug: planData.slug,
        tagline: planData.tagline,
        monthly_price: planData.monthly_price,
        annual_price: planData.annual_price,
        credits_label: planData.credits_label,
        lead_credits: planData.lead_credits ?? undefined,
        email_credits: planData.email_credits ?? undefined,
        trial_days: planData.trial_days,
        ai_features: planData.ai_features,
        advanced_analytics: planData.advanced_analytics,
        analytics_export: planData.analytics_export,
        campaign_features: planData.campaign_features,
        is_popular: planData.is_popular,
        is_active: planData.is_active,
        sort_order: planData.sort_order,
        stripe_price_id_monthly: planData.stripe_price_id_monthly ?? '',
        stripe_price_id_yearly: planData.stripe_price_id_yearly ?? '',
        stripe_product_id: planData.stripe_product_id ?? '',
        currency: planData.currency,
      });
    }
  }, [planData, reset]);

  const leadCredits = watch('lead_credits');
  useEffect(() => {
    const label =
      leadCredits != null && leadCredits !== ('' as unknown)
        ? `${Number(leadCredits).toLocaleString('en-US')} lead credits/mo`
        : 'Unlimited lead credits/mo';
    setValue('credits_label', label);
  }, [leadCredits, setValue]);

  const planMutation = useMutation({
    mutationFn: async (values: PlanFormValues) => {
      const payload = {
        ...values,
        lead_credits: values.lead_credits !== undefined && values.lead_credits !== null && !isNaN(values.lead_credits as number)
          ? values.lead_credits : null,
        email_credits: values.email_credits !== undefined && values.email_credits !== null && !isNaN(values.email_credits as number)
          ? values.email_credits : null,
        stripe_price_id_monthly: values.stripe_price_id_monthly || null,
        stripe_price_id_yearly: values.stripe_price_id_yearly || null,
        stripe_product_id: values.stripe_product_id || null,
      };
      if (isEdit) {
        const res = await apiClient.patch(`/plans/${id}`, payload);
        return res.data.data as Plan;
      } else {
        const res = await apiClient.post('/plans', payload);
        return res.data.data as Plan;
      }
    },
    onSuccess: (saved) => {
      toast.success(isEdit ? 'Plan updated' : 'Plan created');
      qc.invalidateQueries({ queryKey: ['admin-plans'] });
      if (!isEdit) {
        navigate(`/plans/${saved.id}/edit`);
      } else {
        qc.invalidateQueries({ queryKey: ['admin-plan', id] });
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Plan features ──────────────────────────────────────────────────────────
  const { data: featuresData, refetch: refetchFeatures } = useQuery({
    queryKey: ['admin-plan-features', id],
    queryFn: async () => {
      const res = await apiClient.get(`/plans/${id}/features`);
      return res.data.data.features as PlanFeature[];
    },
    enabled: isEdit,
  });

  const features: PlanFeature[] = featuresData ?? planData?.features ?? [];

  const addFeature = async (text: string, included: boolean) => {
    if (!id) return;
    await apiClient.post(`/plans/${id}/features`, {
      text,
      included,
      sort_order: features.length + 1,
    });
    refetchFeatures();
    toast.success('Feature added');
  };

  const updateFeature = async (featureId: string, data: Partial<PlanFeature>) => {
    try {
      await apiClient.patch(`/features/${featureId}`, data);
      refetchFeatures();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const deleteFeature = async (featureId: string) => {
    try {
      await apiClient.delete(`/features/${featureId}`);
      refetchFeatures();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  // ── Comparison rows ────────────────────────────────────────────────────────
  const { data: comparisonData, refetch: refetchComparison } = useQuery({
    queryKey: ['admin-pricing-comparison'],
    queryFn: async () => {
      const res = await apiClient.get('/pricing/comparison');
      return res.data.data.rows as ComparisonRow[];
    },
    enabled: isEdit,
  });

  const comparisonRows: ComparisonRow[] = comparisonData ?? [];

  const addComparisonRow = async (featureName: string, value: string) => {
    if (!id) return;
    await apiClient.post('/pricing/comparison', {
      feature: featureName,
      sort_order: comparisonRows.length + 1,
      values: [{ plan_id: id, value }],
    });
    refetchComparison();
    toast.success('Row added');
  };

  const updateComparisonRowValue = async (rowId: string, value: string) => {
    if (!id) return;
    try {
      await apiClient.patch(`/pricing/comparison/${rowId}`, {
        values: [{ plan_id: id, value }],
      });
      refetchComparison();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const updateComparisonRowFeature = async (rowId: string, feature: string) => {
    try {
      await apiClient.patch(`/pricing/comparison/${rowId}`, { feature });
      refetchComparison();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const deleteComparisonRow = async (rowId: string) => {
    try {
      await apiClient.delete(`/pricing/comparison/${rowId}`);
      refetchComparison();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isEdit && planLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/plans')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{isEdit ? `Edit Plan — ${planData?.name ?? ''}` : 'New Plan'}</h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? 'Update plan details, features and comparison table' : 'Create a new pricing plan'}
          </p>
        </div>
      </div>

      {/* ── Section 1: Plan Details ─────────────────────────────────────────── */}
      <div className="rounded-xl border bg-card p-6 space-y-4">
        <h2 className="text-base font-semibold">Plan Details</h2>
        <form onSubmit={handleSubmit((v) => planMutation.mutate(v))} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Plan Name</Label>
              <Input placeholder="Gold" {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Trial Days</Label>
              <Input type="number" min="0" {...register('trial_days')} />
            </div>
          </div>

          <div className="hidden">
            <Input placeholder="gold" {...register('slug')} />
            <Input {...register('credits_label')} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Monthly Price ($)</Label>
              <Input type="number" readOnly className="bg-muted cursor-not-allowed" {...register('monthly_price')} />
            </div>
            <div className="space-y-1.5">
              <Label>Annual Price ($/mo billed yearly)</Label>
              <Input type="number" readOnly className="bg-muted cursor-not-allowed" {...register('annual_price')} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Lead Credits / Month <span className="text-muted-foreground text-xs">(empty = unlimited)</span></Label>
              <Input type="number" min="0" placeholder="500" {...register('lead_credits', { setValueAs: (v) => v === '' ? null : Number(v) })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email Credits / Month <span className="text-muted-foreground text-xs">(empty = unlimited)</span></Label>
              <Input type="number" min="0" placeholder="500" {...register('email_credits', { setValueAs: (v) => v === '' ? null : Number(v) })} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Tagline</Label>
            <Input placeholder="For growing sales teams" {...register('tagline')} />
            {errors.tagline && <p className="text-xs text-destructive">{errors.tagline.message}</p>}
          </div>

          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <p className="text-sm font-medium">Feature Flags</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>AI Features</Label>
                <Select value={watch('ai_features') ? 'yes' : 'no'} onValueChange={(v) => setValue('ai_features', v === 'yes')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Enabled</SelectItem>
                    <SelectItem value="no">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Campaign Features</Label>
                <Select value={watch('campaign_features') ? 'yes' : 'no'} onValueChange={(v) => setValue('campaign_features', v === 'yes')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Enabled</SelectItem>
                    <SelectItem value="no">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Advanced Analytics</Label>
                <Select value={watch('advanced_analytics') ? 'yes' : 'no'} onValueChange={(v) => setValue('advanced_analytics', v === 'yes')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Enabled</SelectItem>
                    <SelectItem value="no">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Analytics Export (CSV)</Label>
                <Select value={watch('analytics_export') ? 'yes' : 'no'} onValueChange={(v) => setValue('analytics_export', v === 'yes')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Enabled</SelectItem>
                    <SelectItem value="no">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className={`grid gap-3 ${isEdit ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <div className="space-y-1.5">
              <Label>Sort Order</Label>
              <Input type="number" min="0" {...register('sort_order')} />
            </div>
            <div className="space-y-1.5">
              <Label>Popular</Label>
              <Select
                value={watch('is_popular') ? 'yes' : 'no'}
                onValueChange={(v) => setValue('is_popular', v === 'yes')}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isEdit && (
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select
                  value={watch('is_active') ? 'active' : 'inactive'}
                  onValueChange={(v) => setValue('is_active', v === 'active')}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="hidden">
            <Input {...register('stripe_price_id_monthly')} />
            <Input {...register('stripe_price_id_yearly')} />
            <Input {...register('stripe_product_id')} />
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={planMutation.isPending}>
              {planMutation.isPending ? 'Saving…' : isEdit ? 'Update Plan' : 'Create Plan & Continue'}
            </Button>
          </div>
        </form>
      </div>

      {/* ── Section 2: Plan Features ─────────────────────────────────────────── */}
      <div className={`rounded-xl border bg-card p-6 space-y-4 ${!isEdit ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Plan Features</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Shown on the plan card on the pricing page</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setAddFeatureOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add Feature
          </Button>
        </div>
        {!isEdit && (
          <p className="text-sm text-muted-foreground italic">Save the plan first to manage features.</p>
        )}
        {isEdit && features.length === 0 && (
          <p className="text-sm text-muted-foreground">No features yet. Click "Add Feature" to start.</p>
        )}
        <div className="space-y-1">
          {features.map((f) => (
            <FeatureRow key={f.id} feature={f} onUpdate={updateFeature} onDelete={deleteFeature} />
          ))}
        </div>
      </div>

      {/* ── Section 3: Comparison Table ──────────────────────────────────────── */}
      <div className={`rounded-xl border bg-card p-6 space-y-4 ${!isEdit ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Comparison Table</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Showing values for <span className="font-medium">{planData?.name ?? 'this plan'}</span> — edit other plans to set their values
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setAddRowOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add Row
          </Button>
        </div>
        {!isEdit && (
          <p className="text-sm text-muted-foreground italic">Save the plan first to manage comparison rows.</p>
        )}
        {isEdit && comparisonRows.length === 0 && (
          <p className="text-sm text-muted-foreground">No comparison rows yet.</p>
        )}
        {isEdit && comparisonRows.length > 0 && (
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground w-1/2">Feature Name</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground w-1/2">Value for {planData?.name}</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {comparisonRows.map((row) => {
                  const myValue = row.values.find((v) => v.plan_id === id);
                  return (
                    <ComparisonRowEditRow
                      key={row.id}
                      row={row}
                      currentValue={myValue?.value ?? ''}
                      onUpdateFeature={updateComparisonRowFeature}
                      onUpdateValue={updateComparisonRowValue}
                      onDelete={deleteComparisonRow}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bottom back button */}
      <div className="flex gap-2 pb-6">
        <Button variant="outline" onClick={() => navigate('/plans')}>
          <ArrowLeft className="h-4 w-4" />
          Back to Plans
        </Button>
        {isEdit && (
          <Badge variant="secondary" className="flex items-center">
            {features.length} features · {comparisonRows.length} comparison rows
          </Badge>
        )}
      </div>

      {/* Add Feature dialog */}
      <AddFeatureDialog
        open={addFeatureOpen}
        onClose={() => setAddFeatureOpen(false)}
        onSave={addFeature}
      />

      {/* Add Comparison Row dialog */}
      <AddComparisonRowDialog
        open={addRowOpen}
        onClose={() => setAddRowOpen(false)}
        onSave={addComparisonRow}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparison row inline edit component
// ---------------------------------------------------------------------------

function ComparisonRowEditRow({
  row,
  currentValue,
  onUpdateFeature,
  onUpdateValue,
  onDelete,
}: {
  row: ComparisonRow;
  currentValue: string;
  onUpdateFeature: (rowId: string, feature: string) => void;
  onUpdateValue: (rowId: string, value: string) => void;
  onDelete: (rowId: string) => void;
}) {
  const [feature, setFeature] = useState(row.feature);
  const [value, setValue] = useState(currentValue);

  // Sync with props after refetch
  useEffect(() => { setFeature(row.feature); }, [row.feature]);
  useEffect(() => { setValue(currentValue); }, [currentValue]);

  return (
    <tr className="hover:bg-muted/10">
      <td className="px-3 py-2">
        <Input
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
          onBlur={() => feature !== row.feature && onUpdateFeature(row.id, feature)}
          className="h-7 text-sm border-0 bg-transparent focus-visible:border focus-visible:bg-background px-1"
        />
      </td>
      <td className="px-3 py-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => value !== currentValue && onUpdateValue(row.id, value)}
          placeholder="e.g. true / false / 500/mo / Unlimited"
          className="h-7 text-sm border-0 bg-transparent focus-visible:border focus-visible:bg-background px-1"
        />
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          onClick={() => onDelete(row.id)}
          className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}
